import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Phone, Hammer } from "lucide-react";
import { CONTACT } from "@/data/site";

const NAV = [
  { label: "Home", hash: "#home" },
  { label: "About", hash: "#about" },
  { label: "Services", hash: "#services" },
  { label: "Detailing", hash: "#detailing" },
  { label: "Gallery", hash: "#gallery" },
  { label: "Reviews", hash: "#reviews" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", hash: "#contact" },
];

// Build an href for a nav item that works from any page.
// Hash links on the home page stay as "#section" (Lenis intercepts).
// Hash links from any other page become "/#section" so the browser navigates
// to home and scrolls to the anchor on load.
function navHref(item, pathname) {
  if (item.path) return item.path;
  if (pathname === "/") return item.hash;
  return `/${item.hash}`;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-9 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="rs-container flex items-center justify-between h-20">
        <a
          href={pathname === "/" ? "#home" : "/"}
          data-testid="brand-logo"
          className="flex items-center gap-2.5"
        >
          <span
            className={`h-10 w-10 rounded-xl grid place-items-center transition-colors ${
              scrolled ? "bg-[#0A192F]" : "bg-white/10 backdrop-blur border border-white/20"
            }`}
          >
            <Hammer className={`h-5 w-5 ${scrolled ? "text-[#F97316]" : "text-[#F97316]"}`} />
          </span>
          <div className={`font-display font-semibold leading-tight ${scrolled ? "text-[#0A192F]" : "text-white"}`}>
            <div className="text-base">RS Handyman</div>
            <div className="text-[10px] tracking-[0.2em] uppercase opacity-70">Services</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.label}
              href={navHref(item, pathname)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${
                scrolled ? "text-slate-700 hover:text-[#F97316]" : "text-white/80 hover:text-white"
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="header-call-link"
            className={`flex items-center gap-2 text-sm font-medium ${
              scrolled ? "text-[#0A192F]" : "text-white"
            }`}
          >
            <Phone className="h-4 w-4 text-[#F97316]" />
            {CONTACT.phone}
          </a>
          <a href={pathname === "/" ? "#contact" : "/#contact"} data-testid="header-cta-estimate" className="rs-btn-primary text-sm">
            Free Estimate
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 rounded-lg ${scrolled ? "text-[#0A192F]" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 shadow-lg"
        >
          <div className="rs-container py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <a
                key={item.label}
                href={navHref(item, pathname)}
                onClick={() => setOpen(false)}
                className="text-slate-800 font-medium py-1"
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              className="flex items-center gap-2 text-slate-700"
              data-testid="mobile-call-link"
            >
              <Phone className="h-4 w-4 text-[#F97316]" />
              {CONTACT.phone}
            </a>
            <a
              href={pathname === "/" ? "#contact" : "/#contact"}
              onClick={() => setOpen(false)}
              className="rs-btn-primary justify-center w-full"
              data-testid="mobile-cta-estimate"
            >
              Free Estimate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
